/**
 * Contract status workflow, split by contract type.
 *
 * One-time:  draft -> pending_signature -> signed -> in_process -> completed
 * Recurring / Ongoing (as_needed): draft -> pending_signature -> active <-> paused -> completed/expired
 * 'cancelled' is reachable from any non-terminal status on both graphs.
 *
 * One-time signed->in_process->completed also happens automatically from job
 * status changes (see 20260716_contract_status_workflow.sql). Ongoing (as_needed)
 * uses the recurring status graph but keeps is_recurring=false so jobs are not
 * auto-generated.
 */

export type ContractWorkflowType = 'one_time' | 'recurring'

export const CONTRACT_STATUS_TRANSITIONS: Record<
  ContractWorkflowType,
  Record<string, string[]>
> = {
  one_time: {
    draft: ['pending_signature', 'signed', 'cancelled'],
    pending_signature: ['signed', 'draft', 'cancelled'],
    signed: ['in_process', 'cancelled'],
    in_process: ['completed', 'cancelled'],
    completed: [],
    cancelled: ['draft'],
    // Legacy statuses one-time contracts should migrate away from
    active: ['in_process', 'completed', 'cancelled'],
    paused: ['in_process', 'cancelled'],
    expired: [],
  },
  recurring: {
    draft: ['pending_signature', 'active', 'cancelled'],
    pending_signature: ['active', 'draft', 'cancelled'],
    active: ['paused', 'completed', 'expired', 'cancelled'],
    paused: ['active', 'cancelled'],
    completed: [],
    cancelled: ['draft'],
    expired: ['active'],
  },
}

/** Statuses shown in create/edit dropdowns per contract type. */
export const CONTRACT_STATUS_OPTIONS: Record<
  ContractWorkflowType,
  { value: string; label: string }[]
> = {
  one_time: [
    { value: 'draft', label: 'Draft' },
    { value: 'pending_signature', label: 'Pending Signature' },
    { value: 'signed', label: 'Signed (waiting to start)' },
    { value: 'in_process', label: 'In Process' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
  ],
  recurring: [
    { value: 'draft', label: 'Draft' },
    { value: 'pending_signature', label: 'Pending Signature' },
    { value: 'active', label: 'Active' },
    { value: 'paused', label: 'Paused' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'expired', label: 'Expired' },
  ],
}

export function contractWorkflowType(contract: {
  is_recurring?: boolean | null
  contract_type?: string | null
}): ContractWorkflowType {
  // Ongoing (as_needed) stays open like recurring (Active/Paused), even though
  // is_recurring is false (no auto visit generation).
  if (
    contract.is_recurring ||
    contract.contract_type === 'recurring' ||
    contract.contract_type === 'as_needed'
  ) {
    return 'recurring'
  }
  return 'one_time'
}

export function getAllowedContractTransitions(
  type: ContractWorkflowType,
  current: string
): string[] {
  return CONTRACT_STATUS_TRANSITIONS[type][current] || []
}

export function getContractStatusColor(status: string): string {
  switch (status) {
    case 'draft':
      return 'bg-gray-100 text-gray-800'
    case 'pending_signature':
      return 'bg-yellow-100 text-yellow-800'
    case 'signed':
      return 'bg-teal-100 text-teal-800'
    case 'in_process':
      return 'bg-yellow-100 text-yellow-800'
    case 'active':
      return 'bg-green-100 text-green-800'
    case 'paused':
      return 'bg-orange-100 text-orange-800'
    case 'completed':
      return 'bg-blue-100 text-blue-800'
    case 'cancelled':
      return 'bg-red-100 text-red-800'
    case 'expired':
      return 'bg-gray-100 text-gray-600'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

export function formatContractStatusLabel(status: string): string {
  return status.replace(/_/g, ' ')
}
